<script setup>
import { useUserStore } from "@store/user";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import NotificationApi from "@api/notifications.api";
import { useLoaderStore } from "@store/loader";
import UnCollapse from "../../../assets/svg/unCollapse.svg?component";
import Collapse from "../../../assets/svg/collapse.svg?component";
import DocumentForm from "@views/documents/Create.vue";
import ProjectForm from "@views/projects/Create.vue";
import cancelModal from "../../../components/cancelModal.vue";
import AfterCreateProjectModal from "../../projects/AfterCreateProjectModal.vue";
import AfterCreateDocumentModal from "../../documents/AfterCreateDocumentModal.vue";

const props = defineProps({
    collapse: { type: Boolean, default: false },
});
const emit = defineEmits(["toggle"]);

// History navigation state
const showHistoryDropdown = ref(false);
const showUserDropdown = ref(false);
const searchCategory = ref("All");
const searchQuery = ref("");

// Mock history data (replace with real data from your app)
const navigationHistory = ref({
    previousPages: [
        {
            id: 1,
            title: "Objective of the laboratory",
            type: "page",
            icon: "person",
        },
        {
            id: 2,
            title: "Tech requirements.pdf",
            type: "document",
            icon: "doc",
        },
        {
            id: 3,
            title: 'Search Results for "FMS"',
            type: "search",
            icon: "search",
        },
        { id: 4, title: "Robert Fox", type: "person", icon: "person" },
    ],
    forwardPages: [
        {
            id: 5,
            title: "Objective of the laboratory",
            type: "page",
            icon: "person",
        },
        {
            id: 6,
            title: "Tech requirements.pdf",
            type: "document",
            icon: "doc",
        },
        { id: 7, title: "Robert Fox", type: "person", icon: "person" },
        {
            id: 8,
            title: "Tech requirements.pdf",
            type: "document",
            icon: "doc",
        },
    ],
});

const loaderStore = useLoaderStore();
const store = useUserStore();

let userId = computed(() => {
    return useUserStore().user.id;
});
const route = useRoute();
const router = useRouter();
const notifyBadge = ref(false);
// Upload / Create modals state
const createDocument = ref(false);
const createProject = ref(false);
const editProject = ref(null);
const showAfterCreateProjectModal = ref(false);
const showAfterCreateDocumentModal = ref(false);
const createdProjectId = ref(null);
const createdDocumentId = ref(null);

const modalTitle = ref("Upload New Document");
const defaultCategory = ref(null);
const defaultAutoProceed = ref(false);
const defaultProjectId = ref(null);
const logout = async () => {
    useUserStore()
        .logout()
        .then(() => {
            localStorage.clear();
            router.push({ name: "login" });
        })
        .catch((error) => {
            console.log(error);
        });
};

let notifications = ref([]);
let unreadCount = ref(0);
let notificationsLength = ref(0);
let filters = ref({
    limit: 3,
    page: 1,
    order: { created_at: "desc" },
    AllNotifications: userId,
});
let paginationData = ref({});

const getNotifications = () => {
    NotificationApi.getUserAllNotification(filters.value).then((response) => {
        notifications.value = response.data.notifications;
        notificationsLength.value = response.data.length;
        unreadCount.value = 0;
        notifications.value.forEach((notification) => {
            if (notification.is_read === false) {
                unreadCount.value += 1;
            }
        });
    });
};

const markAsRead = (notification = "") => {
    if (notification == "") {
        unreadCount.value = 0;
    }
    NotificationApi.markAsRead({
        id: notification ? notification.id : "",
    }).then((response) => {
        getNotifications();
        if (notification != "") {
            router.push({
                name:
                    notification.type === "project"
                        ? "projects.view"
                        : "documents.view",
                params: { id: notification.resource_id },
            });
        }
        getNotifications();
    });
};

const markAsSeen = (notification = "") => {
    if (notification == "") {
        unreadCount.value = 0;
    }
    NotificationApi.markAsSeen({
        id: notification ? notification.id : "",
    }).then((response) => {
        getNotifications();
        if (notification != "") {
            router.push({
                name:
                    notification.type === "project"
                        ? "projects.view"
                        : "documents.view",
                params: { id: notification.resource_id },
            });
        }
        getNotifications();
    });
};

const afterCreateProject = (project) => {
    createProject.value = false ;
  createdProjectId.value = project.id;  
  showAfterCreateProjectModal.value = true; 
};

const afterCreateDocument = (Document) => {
    createDocument.value = false ;
  createdDocumentId.value = Document.id;  
  showAfterCreateDocumentModal.value = true; 
};


onMounted(() => {
    unreadCount.value = 0;
    getNotifications();

    Echo.channel("notification-channel").listen(
        ".notification-event",
        (event) => {
            notifyBadge.value = true;
            getNotifications();
        }
    );

    // Listen for global open events
    window.addEventListener("open-upload-document", (e) => {
        defaultCategory.value = e?.detail?.category ?? null;
        defaultAutoProceed.value = !!e?.detail?.autoProceedToStep2;
        defaultProjectId.value = e?.detail?.projectId ?? null;
        modalTitle.value = "Upload New Document";
        createDocument.value = true;
    });
    window.addEventListener("open-create-project", (e) => {
        editProject.value = e?.detail?.project || null;
        createProject.value = true;
    });
});

watch(fetchNotification, (currentValue) => {
    if (currentValue) {
        getNotifications();
    }
});

const openNotifyPopup = () => {
    notifyBadge.value = false;
    markAsRead("");
};

// History navigation methods
const toggleHistoryDropdown = () => {
    showHistoryDropdown.value = !showHistoryDropdown.value;
    showUserDropdown.value = false;
};

const toggleUserDropdown = () => {
    showUserDropdown.value = !showUserDropdown.value;
    showHistoryDropdown.value = false;
};

const navigateToHistoryItem = (item) => {
    // Handle navigation to history item
    console.log("Navigating to:", item);
    showHistoryDropdown.value = false;
};

const handleSearch = () => {
    // Handle search functionality
    console.log(
        "Searching for:",
        searchQuery.value,
        "in category:",
        searchCategory.value
    );
};

// Dialog close confirmation
const showCancelDialog = ref(false);
const pendingCloseTarget = ref(null);
const openCloseDialog = () => {
    showCancelDialog.value = true;
};
const confirmCancel = () => {
    if (pendingCloseTarget.value === "document") {
        createDocument.value = false;
    } else if (pendingCloseTarget.value === "project") {
        createProject.value = false;
        editProject.value = null;
    }
    showCancelDialog.value = false;
    pendingCloseTarget.value = null;
};
const backToDialog = () => {
    showCancelDialog.value = false;
};

// Element Plus before-close hooks to keep dialog open until user confirms
const onBeforeCloseDocument = () => {
    pendingCloseTarget.value = "document";
    openCloseDialog();
};
const onBeforeCloseProject = () => {
    pendingCloseTarget.value = "project";
    openCloseDialog();
};

// const handleToggle = () => {
//     const newSideBar = document.querySelector('.newSideBar');
//     if (newSideBar) {
//         newSideBar.classList.remove('toggled');
//     }
//     console.log('TopBar toggle clicked, current isCollapsed:', props.isCollapsed);
//     // emit('toggle');
// }

const isCollapsed = ref(props.collapse);

watch(
    () => props.collapse,
    (newValue) => {
        isCollapsed.value = newValue;
    }
);

const handleToggle = () => {
    isCollapsed.value = !isCollapsed.value;
    emit("toggle", isCollapsed.value);
};
</script>

<template>
    <nav class="topbar-nav">
        <div class="topbar-container">
            <!-- Left Side - History Navigation -->
            <div class="nav-left">
                <div class="toggle-btn d-block d-lg-none" @click="handleToggle">
                    <UnCollapse class="nav-link-text" />
                </div>

                <div class="history-navigation">
                    <button
                        class="nav-btn history-btn"
                        :class="{ active: showHistoryDropdown }"
                        @click="toggleHistoryDropdown"
                        title="History"
                    >
                        <img
                            src="../../../assets/img/clock.png"
                            alt="Notification"
                        />
                    </button>
                    <button
                        class="nav-btn"
                        title="Back"
                        @click.prevent="router.back()"
                    >
                        <img
                            src="../../../assets/img/backword.png"
                            alt="Notification"
                        />
                    </button>
                    <button
                        class="nav-btn"
                        title="Forward"
                        @click.prevent="router.go(1)"
                    >
                        <img
                            src="../../../assets/img/forward.png"
                            alt="Notification"
                        />
                    </button>
                    <span class="nav-text">To Navigate</span>
                </div>

                <!-- History Dropdown -->
                <div v-if="showHistoryDropdown" class="history-dropdown">
                    <div class="history-section">
                        <div class="history-header">
                            <span class="section-title">Previous Pages</span>
                            <span class="show-all">show all</span>
                        </div>
                        <div class="history-items">
                            <div
                                v-for="item in navigationHistory.previousPages"
                                :key="item.id"
                                class="history-item"
                                @click="navigateToHistoryItem(item)"
                            >
                                <div class="history-icon">
                                    <i
                                        v-if="item.icon === 'person'"
                                        class="fas fa-user"
                                    ></i>
                                    <i
                                        v-else-if="item.icon === 'doc'"
                                        class="fas fa-file-alt"
                                    ></i>
                                    <i
                                        v-else-if="item.icon === 'search'"
                                        class="fas fa-search"
                                    ></i>
                                </div>
                                <span class="history-title">{{
                                    item.title
                                }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="history-section">
                        <div class="history-header">
                            <span class="section-title">Forward Pages</span>
                            <span class="show-all">show all</span>
                        </div>
                        <div class="history-items">
                            <div
                                v-for="item in navigationHistory.forwardPages"
                                :key="item.id"
                                class="history-item"
                                @click="navigateToHistoryItem(item)"
                            >
                                <div class="history-icon">
                                    <i
                                        v-if="item.icon === 'person'"
                                        class="fas fa-user"
                                    ></i>
                                    <i
                                        v-else-if="item.icon === 'doc'"
                                        class="fas fa-file-alt"
                                    ></i>
                                    <i
                                        v-else-if="item.icon === 'search'"
                                        class="fas fa-search"
                                    ></i>
                                </div>
                                <span class="history-title">{{
                                    item.title
                                }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Middle - Search -->
            <div class="nav-center">
                <div class="search-container">
                    <select v-model="searchCategory" class="search-category">
                        <option value="All">All</option>
                        <option value="Projects">Projects</option>
                        <option value="Documents">Documents</option>
                        <option value="Users">Users</option>
                    </select>
                    <input
                        v-model="searchQuery"
                        class="search-input"
                        placeholder="Ask anything in osharek knowledge base (projects, docs, offering...)"
                        @keyup.enter="handleSearch"
                    />
                    <button class="search-ai-btn" @click="handleSearch">
                        <svg
                            width="17"
                            height="16"
                            viewBox="0 0 17 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                opacity="0.4"
                                d="M7.16569 4.1665C6.95647 4.1665 6.76937 4.29677 6.69676 4.49299L6.35287 5.42235C5.88605 6.68391 5.69634 7.16579 5.34732 7.5148C4.99831 7.86382 4.51643 8.05353 3.25487 8.52035L2.32551 8.86424C2.12929 8.93685 1.99902 9.12395 1.99902 9.33317C1.99902 9.54239 2.12929 9.72949 2.32551 9.8021L3.25488 10.146C4.51643 10.6128 4.99831 10.8025 5.34732 11.1515C5.69634 11.5006 5.88605 11.9824 6.35287 13.244L6.69676 14.1734C6.76937 14.3696 6.95647 14.4998 7.16569 14.4998C7.37491 14.4998 7.56201 14.3696 7.63462 14.1734L7.97851 13.244C8.44533 11.9824 8.63504 11.5006 8.98406 11.1515C9.33307 10.8025 9.81495 10.6128 11.0765 10.146L12.0059 9.80209C12.2021 9.72949 12.3324 9.54239 12.3324 9.33317C12.3324 9.12395 12.2021 8.93685 12.0059 8.86424L11.0765 8.52035C9.81495 8.05353 9.33307 7.86382 8.98406 7.5148C8.63504 7.16579 8.44533 6.68391 7.97851 5.42235L7.63462 4.49299C7.56201 4.29677 7.37491 4.1665 7.16569 4.1665Z"
                                fill="#412CC9"
                            />
                            <path
                                d="M12.499 6.5C12.7081 6.5 12.8951 6.36986 12.9678 6.17383L13.1152 5.77539C13.3244 5.21021 13.3855 5.07646 13.4805 4.98145C13.5755 4.88643 13.7092 4.82535 14.2744 4.61621L14.6729 4.46875C14.8689 4.39606 14.999 4.2091 14.999 4C14.999 3.7909 14.8689 3.60394 14.6729 3.53125L14.2744 3.38379C13.7092 3.17465 13.5755 3.11357 13.4805 3.01855C13.3855 2.92354 13.3244 2.78979 13.1152 2.22461L12.9678 1.82617C12.8951 1.63014 12.7081 1.5 12.499 1.5C12.2899 1.5 12.103 1.63014 12.0303 1.82617L11.8828 2.22461C11.6737 2.78979 11.6126 2.92354 11.5176 3.01855C11.4226 3.11357 11.2888 3.17465 10.7236 3.38379L10.3252 3.53125C10.1292 3.60394 9.99902 3.7909 9.99902 4C9.99902 4.2091 10.1292 4.39605 10.3252 4.46875L10.7236 4.61621C11.2888 4.82535 11.4226 4.88643 11.5176 4.98145C11.6126 5.07646 11.6737 5.21021 11.8828 5.77539L12.0303 6.17383C12.103 6.36986 12.2899 6.5 12.499 6.5Z"
                                fill="#412CC9"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            <!-- Right Side - Notifications and User -->
            <div class="nav-right">
                <!-- Notifications -->
                <div class="nav-item notification-item">
                    <button
                        class="nav-btn notification-btn"
                        @click="openNotifyPopup()"
                    >
                        <svg
                            width="40"
                            height="40"
                            viewBox="0 0 40 40"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <rect
                                x="-0.000976562"
                                width="40"
                                height="40"
                                rx="6"
                                fill="white"
                            />
                            <mask id="path-2-inside-1_9697_9746" fill="white">
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M19.999 11.0415C20.4015 11.0415 20.8543 11.1106 21.2252 11.3535C21.6367 11.6228 21.874 12.0589 21.874 12.604C21.874 12.9908 21.7564 13.391 21.5648 13.7368C24.1919 14.4053 26.1759 16.7176 26.3245 19.5428C26.3321 19.6866 26.3377 19.8243 26.3431 19.9569C26.3616 20.4082 26.3777 20.8011 26.4594 21.1776C26.5572 21.6274 26.7379 21.9811 27.1056 22.2568C27.7467 22.7377 28.124 23.4923 28.124 24.2938C28.124 25.4448 27.218 26.4582 25.999 26.4582H23.0615C22.7719 27.8846 21.5109 28.9582 19.999 28.9582C18.4872 28.9582 17.2261 27.8846 16.9365 26.4582H13.999C12.78 26.4582 11.874 25.4448 11.874 24.2938C11.874 23.5496 12.1994 22.8457 12.7592 22.364L12.8925 22.2568C13.2601 21.9811 13.4409 21.6274 13.5386 21.1776C13.604 20.8764 13.6274 20.5647 13.6436 20.221L13.6549 19.9569C13.6603 19.8243 13.666 19.6866 13.6735 19.5428C13.8222 16.7176 15.8062 14.4053 18.4333 13.7368C18.2417 13.391 18.124 12.9908 18.124 12.604C18.124 12.0589 18.3614 11.6228 18.7729 11.3535C19.1438 11.1106 19.5965 11.0415 19.999 11.0415ZM21.7674 26.4582H18.2307C18.4881 27.1864 19.1826 27.7082 19.999 27.7082C20.8154 27.7082 21.5099 27.1864 21.7674 26.4582ZM19.999 14.7915C17.2949 14.7915 15.0639 16.9082 14.9218 19.6085C14.9162 19.715 14.9117 19.8278 14.9071 19.945C14.8886 20.4117 14.8673 20.9491 14.7601 21.4428C14.6179 22.0978 14.3142 22.7531 13.6425 23.2568C13.3161 23.5016 13.124 23.8858 13.124 24.2938C13.124 24.8063 13.521 25.2082 13.999 25.2082H25.999C26.477 25.2082 26.874 24.8063 26.874 24.2938C26.874 23.8858 26.6819 23.5016 26.3556 23.2568C25.6839 22.7531 25.3802 22.0978 25.2379 21.4428C25.1521 21.0478 25.1214 20.6249 25.103 20.2326L25.0909 19.9449C25.0863 19.8276 25.0819 19.7149 25.0763 19.6085C24.9341 16.9082 22.7031 14.7915 19.999 14.7915ZM19.999 12.2915C19.7112 12.2915 19.5389 12.3459 19.4574 12.3993C19.4239 12.4213 19.4093 12.4404 19.4004 12.4572C19.391 12.4751 19.374 12.5174 19.374 12.604C19.374 12.7976 19.4597 13.06 19.6168 13.2713C19.7784 13.4889 19.9266 13.5415 19.999 13.5415C20.0714 13.5415 20.2196 13.4889 20.3813 13.2713C20.5384 13.06 20.624 12.7976 20.624 12.604C20.624 12.5174 20.607 12.4751 20.5977 12.4572C20.5888 12.4404 20.5742 12.4213 20.5406 12.3993C20.4591 12.3459 20.2869 12.2915 19.999 12.2915Z"
                                />
                            </mask>
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M19.999 11.0415C20.4015 11.0415 20.8543 11.1106 21.2252 11.3535C21.6367 11.6228 21.874 12.0589 21.874 12.604C21.874 12.9908 21.7564 13.391 21.5648 13.7368C24.1919 14.4053 26.1759 16.7176 26.3245 19.5428C26.3321 19.6866 26.3377 19.8243 26.3431 19.9569C26.3616 20.4082 26.3777 20.8011 26.4594 21.1776C26.5572 21.6274 26.7379 21.9811 27.1056 22.2568C27.7467 22.7377 28.124 23.4923 28.124 24.2938C28.124 25.4448 27.218 26.4582 25.999 26.4582H23.0615C22.7719 27.8846 21.5109 28.9582 19.999 28.9582C18.4872 28.9582 17.2261 27.8846 16.9365 26.4582H13.999C12.78 26.4582 11.874 25.4448 11.874 24.2938C11.874 23.5496 12.1994 22.8457 12.7592 22.364L12.8925 22.2568C13.2601 21.9811 13.4409 21.6274 13.5386 21.1776C13.604 20.8764 13.6274 20.5647 13.6436 20.221L13.6549 19.9569C13.6603 19.8243 13.666 19.6866 13.6735 19.5428C13.8222 16.7176 15.8062 14.4053 18.4333 13.7368C18.2417 13.391 18.124 12.9908 18.124 12.604C18.124 12.0589 18.3614 11.6228 18.7729 11.3535C19.1438 11.1106 19.5965 11.0415 19.999 11.0415ZM21.7674 26.4582H18.2307C18.4881 27.1864 19.1826 27.7082 19.999 27.7082C20.8154 27.7082 21.5099 27.1864 21.7674 26.4582ZM19.999 14.7915C17.2949 14.7915 15.0639 16.9082 14.9218 19.6085C14.9162 19.715 14.9117 19.8278 14.9071 19.945C14.8886 20.4117 14.8673 20.9491 14.7601 21.4428C14.6179 22.0978 14.3142 22.7531 13.6425 23.2568C13.3161 23.5016 13.124 23.8858 13.124 24.2938C13.124 24.8063 13.521 25.2082 13.999 25.2082H25.999C26.477 25.2082 26.874 24.8063 26.874 24.2938C26.874 23.8858 26.6819 23.5016 26.3556 23.2568C25.6839 22.7531 25.3802 22.0978 25.2379 21.4428C25.1521 21.0478 25.1214 20.6249 25.103 20.2326L25.0909 19.9449C25.0863 19.8276 25.0819 19.7149 25.0763 19.6085C24.9341 16.9082 22.7031 14.7915 19.999 14.7915ZM19.999 12.2915C19.7112 12.2915 19.5389 12.3459 19.4574 12.3993C19.4239 12.4213 19.4093 12.4404 19.4004 12.4572C19.391 12.4751 19.374 12.5174 19.374 12.604C19.374 12.7976 19.4597 13.06 19.6168 13.2713C19.7784 13.4889 19.9266 13.5415 19.999 13.5415C20.0714 13.5415 20.2196 13.4889 20.3813 13.2713C20.5384 13.06 20.624 12.7976 20.624 12.604C20.624 12.5174 20.607 12.4751 20.5977 12.4572C20.5888 12.4404 20.5742 12.4213 20.5406 12.3993C20.4591 12.3459 20.2869 12.2915 19.999 12.2915Z"
                                fill="#161616"
                            />
                            <path
                                d="M21.2252 11.3535L20.3123 12.7479L20.3124 12.7479L21.2252 11.3535ZM21.5648 13.7368L20.1069 12.929L19.0597 14.8191L21.1538 15.352L21.5648 13.7368ZM26.3245 19.5428L24.6602 19.6304L24.6602 19.6306L26.3245 19.5428ZM26.3431 19.9569L24.6778 20.0249L24.6778 20.0252L26.3431 19.9569ZM26.4594 21.1776L24.8307 21.5312L24.8308 21.5315L26.4594 21.1776ZM27.1056 22.2568L28.1056 20.9235L28.1056 20.9235L27.1056 22.2568ZM23.0615 26.4582V24.7915H21.6992L21.4282 26.1266L23.0615 26.4582ZM16.9365 26.4582L18.5699 26.1266L18.2989 24.7915H16.9365V26.4582ZM12.7592 22.364L11.715 21.0649L11.6932 21.0824L11.672 21.1007L12.7592 22.364ZM12.8925 22.2568L11.8924 20.9235L11.8701 20.9403L11.8483 20.9578L12.8925 22.2568ZM13.5386 21.1776L15.1673 21.5313L15.1673 21.5313L13.5386 21.1776ZM13.6436 20.221L15.3084 20.2994L15.3087 20.2925L13.6436 20.221ZM13.6549 19.9569L15.32 20.0284L15.3202 20.025L13.6549 19.9569ZM13.6735 19.5428L15.3379 19.6304L15.3379 19.6304L13.6735 19.5428ZM18.4333 13.7368L18.8443 15.352L20.9382 14.8191L19.8912 12.9291L18.4333 13.7368ZM18.7729 11.3535L19.6857 12.7479L19.6857 12.7479L18.7729 11.3535ZM21.7674 26.4582L23.3387 27.0136L24.1242 24.7915H21.7674V26.4582ZM18.2307 26.4582V24.7915H15.8739L16.6593 27.0136L18.2307 26.4582ZM14.9218 19.6085L13.2574 19.5209L13.2574 19.521L14.9218 19.6085ZM14.9071 19.945L13.2417 19.8791L13.2417 19.8791L14.9071 19.945ZM14.7601 21.4428L13.1314 21.0891L13.1314 21.0891L14.7601 21.4428ZM13.6425 23.2568L14.6424 24.5902L14.6424 24.5902L13.6425 23.2568ZM26.3556 23.2568L27.3556 21.9235L27.3555 21.9234L26.3556 23.2568ZM25.2379 21.4428L26.8666 21.0891L26.8666 21.0891L25.2379 21.4428ZM25.103 20.2326L23.4378 20.3026L23.4382 20.3104L25.103 20.2326ZM25.0909 19.9449L23.4256 20.0112L23.4257 20.0149L25.0909 19.9449ZM25.0763 19.6085L26.7407 19.5212L26.7406 19.5209L25.0763 19.6085ZM19.4574 12.3993L18.5447 11.0048L18.5441 11.0051L19.4574 12.3993ZM19.4004 12.4572L17.9269 11.6783L17.924 11.6838L19.4004 12.4572ZM19.6168 13.2713L20.9545 12.2772L20.9544 12.277L19.6168 13.2713ZM20.3813 13.2713L19.0437 12.277L19.0435 12.2772L20.3813 13.2713ZM20.5977 12.4572L22.0741 11.6838L22.0711 11.6783L20.5977 12.4572ZM20.5406 12.3993L21.4539 11.0051L21.4533 11.0048L20.5406 12.3993ZM19.999 11.0415V12.7082C20.1092 12.7082 20.1947 12.718 20.255 12.7317C20.3144 12.7452 20.3263 12.7571 20.3123 12.7479L21.2252 11.3535L22.138 9.95901C21.3811 9.46348 20.5542 9.37484 19.999 9.37484V11.0415ZM21.2252 11.3535L20.3124 12.7479C20.3037 12.7423 20.2891 12.7311 20.2724 12.7129C20.2555 12.6946 20.2404 12.6732 20.2285 12.6505C20.2027 12.6014 20.2074 12.5769 20.2074 12.604H21.874H23.5407C23.5407 11.5268 23.0446 10.5525 22.138 9.959L21.2252 11.3535ZM21.874 12.604H20.2074C20.2074 12.667 20.183 12.7917 20.1069 12.929L21.5648 13.7368L23.0226 14.5445C23.3297 13.9903 23.5407 13.3146 23.5407 12.604H21.874ZM21.5648 13.7368L21.1538 15.352C23.09 15.8447 24.5507 17.5505 24.6602 19.6304L26.3245 19.5428L27.9889 19.4553C27.801 15.8847 25.2937 12.9659 21.9758 12.1216L21.5648 13.7368ZM26.3245 19.5428L24.6602 19.6306C24.6671 19.7628 24.6723 19.8906 24.6778 20.0249L26.3431 19.9569L28.0084 19.8889C28.003 19.7579 27.9971 19.6103 27.9889 19.455L26.3245 19.5428ZM26.3431 19.9569L24.6778 20.0252C24.695 20.4444 24.7127 20.9877 24.8307 21.5312L26.4594 21.1776L28.0882 20.8239C28.0427 20.6145 28.0282 20.3719 28.0084 19.8886L26.3431 19.9569ZM26.4594 21.1776L24.8308 21.5315C24.9883 22.2564 25.3336 23.0112 26.1056 23.5902L27.1056 22.2568L28.1056 20.9235C28.099 20.9185 28.1019 20.9199 28.1091 20.928C28.1164 20.9362 28.122 20.9444 28.1248 20.9494C28.1302 20.9587 28.1114 20.931 28.0881 20.8237L26.4594 21.1776ZM27.1056 22.2568L26.1056 23.5901C26.327 23.7562 26.4574 24.0169 26.4574 24.2938H28.124H29.7907C29.7907 22.9678 29.1664 21.7191 28.1056 20.9235L27.1056 22.2568ZM28.124 24.2938H26.4574C26.4574 24.4319 26.4021 24.5635 26.3126 24.6573C26.2304 24.7435 26.1275 24.7915 25.999 24.7915V26.4582V28.1248C28.2119 28.1248 29.7907 26.2902 29.7907 24.2938H28.124ZM25.999 26.4582V24.7915H23.0615V26.4582V28.1248H25.999V26.4582ZM23.0615 26.4582L21.4282 26.1266C21.2932 26.7916 20.7026 27.2915 19.999 27.2915V28.9582V30.6248C22.3191 30.6248 24.2507 28.9776 24.6949 26.7898L23.0615 26.4582ZM19.999 28.9582V27.2915C19.2954 27.2915 18.7049 26.7916 18.5699 26.1266L16.9365 26.4582L15.3032 26.7897C15.7473 28.9776 17.6789 30.6248 19.999 30.6248V28.9582ZM16.9365 26.4582V24.7915H13.999V26.4582V28.1248H16.9365V26.4582ZM13.999 26.4582V24.7915C13.8705 24.7915 13.7677 24.7435 13.6854 24.6573C13.596 24.5635 13.5407 24.4319 13.5407 24.2938H11.874H10.2074C10.2074 26.2902 11.7862 28.1248 13.999 28.1248V26.4582ZM11.874 24.2938H13.5407C13.5407 24.0367 13.6531 23.7936 13.8463 23.6273L12.7592 22.364L11.672 21.1007C10.7456 21.8979 10.2074 23.0624 10.2074 24.2938H11.874ZM12.7592 22.364L13.8033 23.663L13.9366 23.5559L12.8925 22.2568L11.8483 20.9578L11.715 21.0649L12.7592 22.364ZM12.8925 22.2568L13.8925 23.5901C14.6644 23.0112 15.0098 22.2565 15.1673 21.5313L13.5386 21.1776L11.9099 20.8238C11.8866 20.931 11.8679 20.9587 11.8732 20.9494C11.8761 20.9444 11.8817 20.9362 11.889 20.928C11.8962 20.9199 11.899 20.9186 11.8924 20.9235L12.8925 22.2568ZM13.5386 21.1776L15.1673 21.5313C15.2639 21.0864 15.2915 20.6581 15.3084 20.2994L13.6436 20.221L11.9787 20.1426C11.9633 20.4713 11.9441 20.6664 11.9099 20.8239L13.5386 21.1776ZM13.6436 20.221L15.3087 20.2925L15.32 20.0284L13.6549 19.9569L11.9898 19.8855L11.9784 20.1495L13.6436 20.221ZM13.6549 19.9569L15.3202 20.025C15.3256 19.8912 15.3309 19.763 15.3379 19.6304L13.6735 19.5428L12.0092 19.4552C12.001 19.6102 11.995 19.7573 11.9896 19.8888L13.6549 19.9569ZM13.6735 19.5428L15.3379 19.6304C15.4474 17.5505 16.908 15.8447 18.8443 15.352L18.4333 13.7368L18.0223 12.1216C14.7043 12.9659 12.1971 15.8847 12.0092 19.4552L13.6735 19.5428ZM18.4333 13.7368L19.8912 12.9291C19.815 12.7916 19.7907 12.6669 19.7907 12.604H18.124H16.4574C16.4574 13.3147 16.6685 13.9904 16.9754 14.5444L18.4333 13.7368ZM18.124 12.604H19.7907C19.7907 12.5769 19.7954 12.6014 19.7695 12.6505C19.7576 12.6732 19.7425 12.6946 19.7257 12.7129C19.709 12.7311 19.6943 12.7423 19.6857 12.7479L18.7729 11.3535L17.86 9.959C16.9534 10.5525 16.4574 11.5268 16.4574 12.604H18.124ZM18.7729 11.3535L19.6857 12.7479C19.6717 12.7571 19.6837 12.7452 19.743 12.7317C19.8034 12.718 19.8889 12.7082 19.999 12.7082V11.0415V9.37484C19.4438 9.37484 18.617 9.46348 17.86 9.95901L18.7729 11.3535ZM21.7674 26.4582V24.7915H18.2307V26.4582V28.1248H21.7674V26.4582ZM18.2307 26.4582L16.6593 27.0136C17.1447 28.3869 18.4543 29.3748 19.999 29.3748V27.7082V26.0415C19.9109 26.0415 19.8315 25.9859 19.8021 25.9028L18.2307 26.4582ZM19.999 27.7082V29.3748C21.5437 29.3748 22.8533 28.3869 23.3387 27.0136L21.7674 26.4582L20.196 25.9027C20.1666 25.9859 20.0871 26.0415 19.999 26.0415V27.7082ZM19.999 14.7915V13.1248C16.4085 13.1248 13.4462 15.9354 13.2574 19.5209L14.9218 19.6085L16.5862 19.6961C16.6817 17.881 18.1813 16.4582 19.999 16.4582V14.7915ZM14.9218 19.6085L13.2574 19.521C13.2512 19.6402 13.2463 19.7641 13.2417 19.8791L14.9071 19.945L16.5725 20.0109C16.5772 19.8914 16.5812 19.7898 16.5862 19.696L14.9218 19.6085ZM14.9071 19.945L13.2417 19.8791C13.2222 20.3724 13.2025 20.762 13.1314 21.0891L14.7601 21.4428L16.3888 21.7965C16.5322 21.1361 16.555 20.4509 16.5725 20.0109L14.9071 19.945ZM14.7601 21.4428L13.1314 21.0891C13.0878 21.2902 13.0286 21.448 12.9554 21.5751C12.8856 21.6963 12.7903 21.8126 12.6425 21.9235L13.6425 23.2568L14.6424 24.5902C15.7203 23.7819 16.1874 22.7239 16.3888 21.7965L14.7601 21.4428ZM13.6425 23.2568L12.6425 21.9235C11.8964 22.483 11.4574 23.3612 11.4574 24.2938H13.124H14.7907C14.7907 24.4103 14.7358 24.5202 14.6424 24.5902L13.6425 23.2568ZM13.124 24.2938H11.4574C11.4574 25.6725 12.5472 26.8748 13.999 26.8748V25.2082V23.5415C14.4949 23.5415 14.7907 23.9401 14.7907 24.2938H13.124ZM13.999 25.2082V26.8748H25.999V25.2082V23.5415H13.999V25.2082ZM25.999 25.2082V26.8748C27.4509 26.8748 28.5407 25.6724 28.5407 24.2938H26.874H25.2074C25.2074 23.9401 25.5032 23.5415 25.999 23.5415V25.2082ZM26.874 24.2938H28.5407C28.5407 23.3612 28.1017 22.483 27.3556 21.9235L26.3556 23.2568L25.3556 24.5902C25.2622 24.5201 25.2074 24.4103 25.2074 24.2938H26.874ZM26.3556 23.2568L27.3555 21.9234C27.2078 21.8126 27.1124 21.6963 27.0427 21.5751C26.9695 21.448 26.9103 21.2902 26.8666 21.0891L25.2379 21.4428L23.6092 21.7966C23.8106 22.7239 24.2777 23.7819 25.3557 24.5902L26.3556 23.2568ZM25.2379 21.4428L26.8666 21.0891C26.812 20.8374 26.7855 20.5307 26.7679 20.1547L25.103 20.2326L23.4382 20.3104C23.4573 20.7191 23.4923 21.2582 23.6093 21.7966L25.2379 21.4428ZM25.103 20.2326L26.7682 20.1626L26.7561 19.8749L25.0909 19.9449L23.4257 20.0149L23.4378 20.3026L25.103 20.2326ZM25.0909 19.9449L26.7563 19.8787C26.7518 19.7646 26.7469 19.6404 26.7407 19.5212L25.0763 19.6085L23.4119 19.6958C23.4168 19.7895 23.4208 19.8905 23.4256 20.0112L25.0909 19.9449ZM25.0763 19.6085L26.7406 19.5209C26.5519 15.9354 23.5895 13.1248 19.999 13.1248V14.7915V16.4582C21.8167 16.4582 23.3163 17.881 23.4119 19.6961L25.0763 19.6085ZM19.999 12.2915V10.6248C19.5577 10.6248 19.0118 10.6991 18.5447 11.0048L19.4574 12.3993L20.3702 13.7938C20.2778 13.8543 20.1982 13.8904 20.145 13.9113C20.0898 13.933 20.0467 13.9446 20.0202 13.9506C19.9683 13.9624 19.9571 13.9582 19.999 13.9582V12.2915ZM19.4574 12.3993L18.5441 11.0051C18.2959 11.1678 18.0788 11.391 17.9269 11.6783L19.4004 12.4572L20.8738 13.2362C20.8187 13.3404 20.7428 13.4549 20.6399 13.5654C20.5424 13.6702 20.4464 13.7439 20.3707 13.7934L19.4574 12.3993ZM19.4004 12.4572L17.924 11.6838C17.7732 11.9717 17.7074 12.2858 17.7074 12.604H19.374H21.0407C21.0407 12.6695 21.0343 12.7636 21.0082 12.8771C20.9809 12.9964 20.9368 13.1159 20.8767 13.2306L19.4004 12.4572ZM19.374 12.604H17.7074C17.7074 13.201 17.9371 13.8054 18.2792 14.2656L19.6168 13.2713L20.9544 12.277C20.9829 12.3153 20.9979 12.3472 21.008 12.3759C21.0137 12.3921 21.0209 12.416 21.0272 12.4487C21.0331 12.4801 21.0407 12.5326 21.0407 12.604H19.374ZM19.6168 13.2713L18.279 14.2654C18.6256 14.7318 19.2146 15.2082 19.999 15.2082V13.5415V11.8748C20.3345 11.8748 20.576 11.984 20.7079 12.0643C20.8541 12.1533 20.9296 12.2436 20.9545 12.2772L19.6168 13.2713ZM19.999 13.5415V15.2082C20.7835 15.2082 21.3725 14.7318 21.719 14.2654L20.3813 13.2713L19.0435 12.2772C19.0685 12.2436 19.1439 12.1533 19.2901 12.0643C19.4221 11.984 19.6635 11.8748 19.999 11.8748V13.5415ZM20.3813 13.2713L21.7189 14.2656C22.0609 13.8054 22.2907 13.201 22.2907 12.604H20.624H18.9574C18.9574 12.5326 18.9649 12.4801 18.9709 12.4487C18.9771 12.416 18.9843 12.3921 18.9901 12.3759C19.0002 12.3472 19.0152 12.3153 19.0437 12.277L20.3813 13.2713ZM20.624 12.604H22.2907C22.2907 12.2858 22.2248 11.9717 22.074 11.6838L20.5977 12.4572L19.1213 13.2306C19.0613 13.1159 19.0171 12.9964 18.9898 12.8771C18.9638 12.7636 18.9574 12.6695 18.9574 12.604H20.624ZM20.5977 12.4572L22.0711 11.6783C21.9192 11.391 21.7022 11.1678 21.4539 11.0051L20.5406 12.3993L19.6273 13.7934C19.5516 13.7439 19.4556 13.6702 19.3581 13.5654C19.2553 13.4549 19.1793 13.3404 19.1242 13.2362L20.5977 12.4572ZM20.5406 12.3993L21.4533 11.0048C20.9863 10.6991 20.4404 10.6248 19.999 10.6248V12.2915V13.9582C20.0409 13.9582 20.0297 13.9624 19.9778 13.9506C19.9513 13.9446 19.9083 13.933 19.853 13.9113C19.7998 13.8904 19.7203 13.8543 19.6279 13.7938L20.5406 12.3993Z"
                                fill="#96959D"
                                mask="url(#path-2-inside-1_9697_9746)"
                            />
                        </svg>

                        <span
                            v-if="notifyBadge && unreadCount > 0"
                            class="notification-badge"
                        >
                            {{ unreadCount > 99 ? "+99" : unreadCount }}
                        </span>
                    </button>

                    <!-- Notifications Dropdown -->
                    <div class="notification-dropdown" v-if="notifyBadge">
                        <div class="notification-header">
                            <h6>Notifications</h6>
                        </div>
                        <div
                            v-if="loaderStore.isLoading"
                            class="notification-loading"
                        >
                            <i class="fas fa-spinner fa-spin"></i>
                        </div>
                        <div
                            v-else-if="!loaderStore.isLoading"
                            class="notification-list"
                        >
                            <div
                                v-for="(notification, index) in notifications"
                                :key="index"
                                class="notification-item"
                                :class="{
                                    unread:
                                        notification.resource_id != '' &&
                                        notification.is_seen == false,
                                }"
                                @click="markAsSeen(notification)"
                            >
                                <div class="notification-icon">
                                    <div class="icon-circle">
                                        <i
                                            v-if="
                                                notification.status_type ===
                                                'approve'
                                            "
                                            class="fas fa-check text-success"
                                        ></i>
                                        <i
                                            v-else-if="
                                                notification.status_type ===
                                                'reject'
                                            "
                                            class="fas fa-times text-danger"
                                        ></i>
                                        <i
                                            v-else
                                            class="fas fa-bell text-warning"
                                        ></i>
                                    </div>
                                </div>
                                <div class="notification-content">
                                    <div class="notification-title">
                                        {{ notification.title }}
                                    </div>
                                    <div class="notification-time">
                                        {{ notification.created }}
                                    </div>
                                </div>
                                <div
                                    v-if="
                                        notification.resource_id != '' &&
                                        notification.is_seen == false
                                    "
                                    class="notification-indicator"
                                ></div>
                            </div>
                            <div
                                v-if="notifications.length > 0"
                                class="view-all-notifications"
                            >
                                <router-link to="/notifications"
                                    >View all notifications</router-link
                                >
                            </div>
                        </div>
                        <div v-else class="no-notifications">
                            <h6>No New Notifications Yet</h6>
                        </div>
                    </div>
                </div>

                <!-- User Profile -->
                <div class="nav-item user-item">
                    <button
                        class="nav-btn user-btn"
                        @click="toggleUserDropdown"
                    >
                        <img
                            class="user-avatar"
                            :src="
                                useUserStore().getUserData.avatar ||
                                '../../../assets/img/default-avatar.jpg'
                            "
                            alt="User Avatar"
                        />
                    </button>

                    <!-- User Dropdown -->
                    <div v-if="showUserDropdown" class="user-dropdown">
                        <div class="user-info">
                            <div class="user-avatar-large">
                                <img
                                    :src="
                                        useUserStore().getUserData.avatar ||
                                        '../../../assets/img/default-avatar.jpg'
                                    "
                                    alt="User Avatar"
                                />
                                <div class="online-indicator"></div>
                            </div>
                            <div class="user-details">
                                <div class="user-name">
                                    {{
                                        useUserStore().getUserData.name ||
                                        "User Name"
                                    }}
                                </div>
                                <div class="user-email">
                                    {{
                                        useUserStore().getUserData.email ||
                                        "user@example.com"
                                    }}
                                </div>
                            </div>
                        </div>
                        <div class="user-menu">
                            <div class="menu-item">
                                <i class="fas fa-user"></i>
                                <span>View profile</span>
                            </div>
                            <div class="menu-item">
                                <i class="fas fa-bookmark"></i>
                                <span>Saved Search</span>
                            </div>
                            <div class="menu-item">
                                <i class="fas fa-cog"></i>
                                <span>Settings</span>
                            </div>
                            <div class="menu-item logout-item" @click="logout">
                                <i class="fas fa-sign-out-alt"></i>
                                <span>Log out</span>
                                <span class="shortcut">~↑Q</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </nav>

    <!-- Global Modals moved from Sidebar -->
    <el-dialog
        v-model="createDocument"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :title="modalTitle"
        class="documents-modal"
        destroy-on-close
        :before-close="null"
    >
        <DocumentForm
            :default-category="defaultCategory"
            :default-project-id="defaultProjectId"
            :auto-proceed-to-step2="defaultAutoProceed"
            @update:title="modalTitle = $event"
            @create="afterCreateDocument"
        />
    </el-dialog>

    <el-dialog
        v-model="createProject"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :title="
            editProject
                ? $t('messages.edit_project')
                : $t('messages.add_project')
        "
        class="create-project-modal"
        destroy-on-close
    >
        <!-- :before-close="editProject ? onBeforeCloseProject : null" -->
        <ProjectForm :proj="editProject" @create="afterCreateProject" @update="createProject = false" />
    </el-dialog>

    <cancelModal
        v-model="showCancelDialog"
        :text="
            pendingCloseTarget === 'document'
                ? $t('messages.document_creation')
                : editProject
                ? $t('messages.project_editing')
                : $t('messages.project_creation')
        "
        @cancel="confirmCancel"
        @cancelButtonClose="backToDialog"
    />

    <AfterCreateProjectModal
        v-model="showAfterCreateProjectModal"
        :project-id="createdProjectId"
    />

    <AfterCreateDocumentModal
        v-model="showAfterCreateDocumentModal"
        :document-id="createdDocumentId"
    />

</template>
